import React, { useState } from "react";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import WatchIcon from "@mui/icons-material/Watch";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FieldLabel from "../FieldLabel/FieldLabel";
import "./TextInput.scss";

const iconMap = {
  mail: MailOutlineIcon,
  lock: LockOutlinedIcon,
  user: PersonOutlineIcon,
  watch: WatchIcon,
};

const ICON_COLOR = "#707070";
const VERIFIED_COLOR = "#279191";

// Single MUI TextField-based input replacing FormInputs (icon + password
// toggle) and LayoutInputs (plain labeled input). One component, one visual
// language, used across every form in both portals. Label sits outside/above
// the field (not a floating MUI label) and both the left icon and the
// password-toggle icon share the same MUI icon set and size.
const TextInput = ({
  title,
  type,
  value,
  onChange,
  name,
  placeholder,
  icon,
  prefixText,
  maxLength,
  min,
  max,
  step,
  disabled,
  onKeyDown,
  multiline,
  rows,
  error,
  helperText,
  endAction,
}) => {
  const IconComponent = iconMap[icon];
  const isPassword = type === "password";
  const isNumber = type === "number";
  const [showPassword, setShowPassword] = useState(false);

  // Number fields with a min of 0 (Active/Sleep Time, etc.) shouldn't accept
  // negative values - block the "-" keystroke and reject a pasted negative.
  const blocksNegative = isNumber && Number(min) === 0;

  const handleKeyDown = (e) => {
    if (isNumber && (e.key === "e" || e.key === "E" || e.key === "+")) {
      e.preventDefault();
    } else if (blocksNegative && e.key === "-") {
      e.preventDefault();
    }
    onKeyDown?.(e);
  };

  const handleChange = (e) => {
    if (blocksNegative && e.target.value !== "" && Number(e.target.value) < 0) {
      return;
    }
    // maxLength has no effect on <input type="number"> (unlike type="text"),
    // so enforce it manually here to keep existing fields (e.g. Mobile
    // Number) capped the same way they were before.
    if (isNumber && maxLength && e.target.value.length > maxLength) {
      return;
    }
    onChange?.(e);
  };

  const renderEndAdornment = () => {
    if (isPassword && !disabled) {
      return (
        <InputAdornment position="end">
          <IconButton
            aria-label={showPassword ? "Hide password" : "Show password"}
            onClick={() => setShowPassword((prev) => !prev)}
            edge="end"
          >
            {showPassword ? (
              <VisibilityOff sx={{ color: ICON_COLOR, fontSize: 22 }} />
            ) : (
              <Visibility sx={{ color: ICON_COLOR, fontSize: 22 }} />
            )}
          </IconButton>
        </InputAdornment>
      );
    }

    if (endAction) {
      const { text, onClick, disabled: actionDisabled, loading, verified } = endAction;

      return (
        <InputAdornment position="end">
          <Button
            type="button"
            size="small"
            onClick={onClick}
            disabled={actionDisabled || loading || verified}
            startIcon={
              loading ? (
                <CircularProgress size={14} thickness={5} />
              ) : verified ? (
                <CheckCircleIcon sx={{ fontSize: 18 }} />
              ) : undefined
            }
            sx={{
              minWidth: "auto",
              padding: "4px 10px",
              fontWeight: 600,
              textTransform: "none",
              color: verified ? VERIFIED_COLOR : undefined,
              "&.Mui-disabled": {
                color: verified ? VERIFIED_COLOR : undefined,
              },
            }}
          >
            {text}
          </Button>
        </InputAdornment>
      );
    }

    return undefined;
  };

  return (
    <div className="text-input">
      {title && <FieldLabel htmlFor={name}>{title}</FieldLabel>}
      <TextField
        id={name}
        name={name}
        type={isPassword ? (showPassword ? "text" : "password") : type || "text"}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        onKeyDown={handleKeyDown}
        multiline={multiline}
        rows={multiline ? rows || 3 : undefined}
        inputProps={
          maxLength || min != null || max != null || step != null
            ? { maxLength, min, max, step }
            : undefined
        }
        error={error}
        helperText={helperText}
        InputProps={{
          startAdornment: IconComponent ? (
            <InputAdornment position="start">
              <IconComponent sx={{ color: ICON_COLOR, fontSize: 22 }} />
              {prefixText}
            </InputAdornment>
          ) : prefixText ? (
            <InputAdornment position="start">{prefixText}</InputAdornment>
          ) : undefined,
          endAdornment: renderEndAdornment(),
        }}
      />
    </div>
  );
};

export default TextInput;
